import type { Server } from 'miragejs';
import { response, readApiTransform } from 'mocks/helpers';
import { HTTP_RESPONSE_CODES } from 'mocks/api';
import { t } from '@lingui/macro';
import type { View } from 'types/views';

// Initial views data shared between seeds and reset endpoint
export const initialViewsData = [
  {
    id: 1,
    viewName: 'View 1',
    colorIndicator: '#3B82F6',
    displayOrder: 1,
    isDefault: true,
    isShared: false,
    filterCriteria: { taskName: 1 },
    createdAt: new Date('2024-01-01').toISOString(),
    updatedAt: new Date('2024-01-01').toISOString(),
  },
  {
    id: 2,
    viewName: 'View 2',
    colorIndicator: '#10B981',
    displayOrder: 2,
    isDefault: false,
    isShared: false,
    filterCriteria: { taskName: 2 },
    createdAt: new Date('2024-01-02').toISOString(),
    updatedAt: new Date('2024-01-02').toISOString(),
  },
  {
    id: 3,
    viewName: 'View 3',
    colorIndicator: '#F59E0B',
    displayOrder: 3,
    isDefault: false,
    isShared: false,
    filterCriteria: { taskName: 3 },
    createdAt: new Date('2024-01-03').toISOString(),
    updatedAt: new Date('2024-01-03').toISOString(),
  },
];

// Helper to create initial views in Mirage schema
export const seedInitialViews = (server: Server) => {
  initialViewsData.forEach(viewData => {
    (server as unknown as { create: (type: string, data: unknown) => void }).create(
      'view',
      viewData
    );
  });
};

// MirageJS assigns string model ids, but the `View` contract (and the production API)
// uses a numeric `id`. The ViewDropdown resolves the selected view by strict-equality
// (`view.id === selectedViewId`) against a number parsed from the URL, so string ids
// would silently break selection state in mock mode. Serialize every view response with
// a numeric id so mock mode matches production.
const toView = (model: unknown): Record<string, unknown> => {
  const m = model as { id: string | number; attrs: Record<string, unknown> };
  return { ...m.attrs, id: Number(m.id) };
};

// Additive, opt-in extra-view fixtures for the dl-66886 restore/storage/resilience epic
// (Testmo folder dl-59076). The shared 3-default-view seed above is asserted on by exact
// count (`toHaveCount(3)`) across many existing specs, so it cannot grow a 4th entry.
// These extras instead live in a DEDICATED localStorage key, read fresh on every request
// rather than persisted into the Mirage schema — which is why they survive a full page
// reload (the in-memory schema resets on every navigation) while leaving every existing
// spec byte-for-byte unchanged when the key is absent. A spec opts in with
// `commons/taskboard/viewsFixtureSeed.helpers.ts#seedExtraViews`.
type ExtraViewSeed = {
  id: number;
  entityType?: string | null;
  viewName: string;
  filterCriteria: Record<string, unknown>;
};

const EXTRA_VIEWS_SEED_KEY = 'e2e-extra-views-seed';

const readExtraViewsSeed = (): ExtraViewSeed[] => {
  try {
    const raw = localStorage.getItem(EXTRA_VIEWS_SEED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const toExtraView = (seed: ExtraViewSeed): Record<string, unknown> => ({
  id: seed.id,
  viewName: seed.viewName,
  colorIndicator: '#3B82F6',
  displayOrder: 1000 + seed.id,
  isDefault: false,
  isShared: false,
  filterCriteria: seed.filterCriteria,
  createdAt: new Date('2024-06-01').toISOString(),
  updatedAt: new Date('2024-06-01').toISOString(),
});

// Additive, opt-in e2e-only key: with the key absent or falsy, list order is byte-for-byte
// unchanged. Set to sort the combined list newest-id-first instead of Mirage's natural
// insertion order, so a spec can prove `ViewDropdown.tsx`'s "identify the just-created view by
// id-set diff, not array position" behaviour (dl-66981/#1807683) — a naive `views[views.length -
// 1]` read would pick the OLDEST view once the list is reordered like this.
const VIEW_ORDER_SCRAMBLE_KEY = 'e2e-view-order-scramble';

export const viewsEndpoints = (server: Server) => {
  // Get all views for an entity type. Views created through this mock carry the
  // `entityType` they were created under and only list under it (the production API
  // is per entity type); the seeded legacy views carry none and stay visible to every
  // entity type — the styles/items/taskboard e2e specs depend on that seed.
  server.get('/api/views/:entityType', async (schema, request) => {
    // Opt-in artificial delay (DL-66807 — loading-state spec) so a spec can observe the
    // ViewDropdown's `CircularProgress` deterministically instead of racing Mirage's ambient
    // response time. Additive: with no override active in localStorage this is a no-op.
    const transform = readApiTransform();
    if (transform?.url && request.url.includes(transform.url) && transform.delay) {
      await new Promise(resolve => setTimeout(resolve, transform.delay));
    }

    const { entityType } = request.params;
    const views = schema
      .all('view')
      .models.filter(m => {
        const owner = (m as { attrs: Record<string, unknown> }).attrs.entityType;
        return owner == null || owner === entityType;
      })
      .map(toView);
    const extraViews = readExtraViewsSeed()
      .filter(seed => seed.entityType == null || seed.entityType === entityType)
      .map(toExtraView);
    const combinedViews = [...views, ...extraViews];
    const orderedViews =
      localStorage.getItem(VIEW_ORDER_SCRAMBLE_KEY) === 'true'
        ? [...combinedViews].sort((a, b) => Number(b.id) - Number(a.id))
        : combinedViews;
    return response(
      request.url,
      { views: orderedViews },
      {
        statusCode: HTTP_RESPONSE_CODES.OK,
        code: 'ok',
        status: 'success',
      }
    );
  });

  // Get a specific view by ID
  server.get('/api/views/:entityType/:viewId', (schema, request) => {
    const { entityType, viewId } = request.params;
    const view = schema.find('view', viewId);

    if (view) {
      return response(request.url, toView(view), {
        statusCode: HTTP_RESPONSE_CODES.OK,
        code: 'ok',
        status: 'success',
      });
    }

    const extraView = readExtraViewsSeed().find(
      seed =>
        String(seed.id) === String(viewId) &&
        (seed.entityType == null || seed.entityType === entityType)
    );
    if (extraView) {
      return response(request.url, toExtraView(extraView), {
        statusCode: HTTP_RESPONSE_CODES.OK,
        code: 'ok',
        status: 'success',
      });
    }

    return response(request.url, undefined, {
      statusCode: HTTP_RESPONSE_CODES.ENTITY_NOT_FOUND,
      code: 'view_not_found',
      status: 'fail',
      error: {
        details: [
          {
            type: t`not_found`,
            value: t`Could not find a view with the id of ${viewId}`,
          },
        ],
      },
    });
  });

  // Create a new view
  server.post('/api/views/:entityType', (schema, request) => {
    const body = JSON.parse(request.requestBody);
    const currentViews = schema.all('view').models;

    const newView = (
      schema as unknown as { create: (type: string, data: unknown) => { attrs: View } }
    ).create('view', {
      entityType: request.params.entityType,
      viewName: body.viewName || `View ${currentViews.length + 1}`,
      colorIndicator: body.colorIndicator || '#3B82F6',
      displayOrder: currentViews.length + 1,
      isDefault: false,
      isShared: body.isShared || false,
      filterCriteria: body.filterCriteria || {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    return response(request.url, toView(newView), {
      statusCode: HTTP_RESPONSE_CODES.CREATED,
      code: 'view_created',
      status: 'success',
    });
  });

  // Update a view
  server.put('/api/views/:entityType/:viewId', (schema, request) => {
    const { viewId } = request.params;
    const body = JSON.parse(request.requestBody);
    const view = schema.find('view', viewId);

    if (!view) {
      return response(request.url, undefined, {
        statusCode: HTTP_RESPONSE_CODES.ENTITY_NOT_FOUND,
        code: 'view_not_found',
        status: 'fail',
        error: {
          details: [
            {
              type: t`not_found`,
              value: t`Could not find a view with the id of ${viewId}`,
            },
          ],
        },
      });
    }

    view.update({
      ...body,
      updatedAt: new Date().toISOString(),
    });

    return response(request.url, toView(view), {
      statusCode: HTTP_RESPONSE_CODES.UPDATED,
      code: 'view_updated',
      status: 'success',
    });
  });

  // Reorder views
  server.put('/api/views/:entityType/order', (schema, request) => {
    const body = JSON.parse(request.requestBody);
    const { viewOrders } = body;

    if (viewOrders && Array.isArray(viewOrders)) {
      viewOrders.forEach((order: { id: number; displayOrder: number }) => {
        const view = (
          schema as unknown as {
            find: (
              type: string,
              id: number
            ) => { update: (data: { displayOrder: number }) => void } | null;
          }
        ).find('view', order.id);
        if (view) {
          view.update({ displayOrder: order.displayOrder });
        }
      });
    }

    return response(
      request.url,
      { success: true },
      {
        statusCode: HTTP_RESPONSE_CODES.UPDATED,
        code: 'views_reordered',
        status: 'success',
      }
    );
  });

  // Delete a view
  server.delete('/api/views/:entityType/:viewId', (schema, request) => {
    const { viewId } = request.params;
    const view = schema.find('view', viewId);

    if (!view) {
      return response(request.url, undefined, {
        statusCode: HTTP_RESPONSE_CODES.ENTITY_NOT_FOUND,
        code: 'view_not_found',
        status: 'fail',
        error: {
          details: [
            {
              type: t`not_found`,
              value: t`Could not find a view with the id of ${viewId}`,
            },
          ],
        },
      });
    }

    view.destroy();

    // Return 200 + a body (not 204/no-content): the shared apisauce adapter rejects a
    // response with no `data` (see lib/api adapterBase), which would surface a delete as
    // a failure. The production envelope always carries a `data` payload.
    return response(
      request.url,
      { success: true },
      {
        statusCode: HTTP_RESPONSE_CODES.OK,
        code: 'view_deleted',
        status: 'success',
      }
    );
  });

  // Setup endpoint for testing - set up a specific number of views
  server.post('/api/views/setup', (schema, request) => {
    const { count } = JSON.parse(request.requestBody);

    // Clear all existing views
    schema.all('view').models.forEach(view => view.destroy());

    // Insert new views into DB
    const views = Array.from({ length: count }, (_, i) => ({
      viewName: `View ${i + 1}`,
      colorIndicator: '#3B82F6',
      displayOrder: i + 1,
      isDefault: i === 0,
      isShared: false,
      filterCriteria: { taskName: i + 1 },
      createdAt: new Date('2024-01-01').toISOString(),
      updatedAt: new Date('2024-01-01').toISOString(),
    }));

    schema.db.views.insert(views);

    return response(
      request.url,
      { success: true, message: `Views set up with ${count} views` },
      {
        statusCode: HTTP_RESPONSE_CODES.OK,
        code: 'views_setup_complete',
        status: 'success',
      }
    );
  });

  // Reset endpoint for testing - restore initial state
  server.post('/api/views/reset', (schema, request) => {
    // Clear all existing views
    schema.all('view').models.forEach(view => view.destroy());

    // Recreate initial 3 views using shared helper
    seedInitialViews(server);

    return response(
      request.url,
      { success: true, message: 'Views reset to initial state' },
      {
        statusCode: HTTP_RESPONSE_CODES.OK,
        code: 'views_reset_complete',
        status: 'success',
      }
    );
  });
};
